/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import makeComponentTrashable from 'trashable-react';
import PropTypes from 'prop-types';
import { useTopbarHeight, useWindowHeight, useTranslation } from 'Shared/util/custom-hooks';
import useUserContext from 'Contexts/user-context';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { joinClassNames as c } from 'Shared/util/styles';
import UserEditView, { TabOptions } from 'Pages/user-administration/user-edit/user-edit-view';
import { UserEditStatus } from 'Pages/user-administration/user-administration-utils';
import ReturnTitle from 'Shared/components/return-title';
import TextLink from 'Shared/components/text-link';
import Animation from 'Shared/components/animation';
import InlineSvg from 'Shared/components/inline-svg';
import styles from './header-menu.css';


const displayMenuSection = (bool, direction) => (bool ? styles.enabled : (direction === 'disabled_left' ? styles.disabled_left : styles.disabled_right));


const HeaderMenu = ({
  show,
  toggleShow,
}) => {
  const {
    user, setActiveCompany, logOut
  } = useUserContext();
  const history = useHistory();
  const [selectOrgTab, setSelectOrgTab] = useState(false);
  const [userEditViewStatus, setUserEditViewStatus] = useState(UserEditStatus.NONE);
  const [userEditViewStatusTab, setUserEditViewStatusTab] = useState();
  const [maxHeightContainer, setMaxHeightContainer] = useState(null);
  const [heightOrgTab, setHeightOrgTab] = useState(null);
  const [containerNode, setContainerNode] = useState(null);
  const topbarHeight = useTopbarHeight();
  const windowHeight = useWindowHeight();
  const t = useTranslation(x => x.header);

  const { isAdmin } = user.activeCompany.role;

  const containerCallbackRef = useCallback((node) => setContainerNode(node), []);

  useEffect(() => {
    if (show) {
      setUserEditViewStatus(UserEditStatus.NONE);
    } else {
      setSelectOrgTab(false);
    }
  }, [show]);

  useEffect(() => {
    if (!show || !containerNode) return;

    const computedStyle = getComputedStyle(containerNode);
    const containerPaddingTopAndBottom = (parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom));
    const containerScrollHeight = containerNode.scrollHeight;
    const totalMaxHeight = (topbarHeight + containerScrollHeight);

    if (windowHeight > totalMaxHeight) {
      const containerInnerHeight = (containerScrollHeight - containerPaddingTopAndBottom);
      setMaxHeightContainer(containerScrollHeight);
      setHeightOrgTab(containerInnerHeight);
    } else {
      const smallerContainerHeight = (windowHeight - topbarHeight);
      setMaxHeightContainer(smallerContainerHeight);
      setHeightOrgTab(smallerContainerHeight - containerPaddingTopAndBottom);
    }
  }, [show, containerNode, windowHeight, topbarHeight]);


  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sidebar = params.get('sidebar');

    if (sidebar === 'activate-eid') {
      setUserEditViewStatus(UserEditStatus.EDIT_SELF);
      setUserEditViewStatusTab(TabOptions.BANKID);
    }
  }, []);

  useEffect(() => {
    if (!containerNode) return undefined;

    if (show) {
      disableBodyScroll(containerNode, {
        reserveScrollBarGap: true,
        allowTouchMove: (el) => (el.closest('[data-body-scroll-lock-ignore]') !== null), // 210726 Fix for iOS Safari scroll
      });
    } else {
      enableBodyScroll(containerNode);
    }

    return () => enableBodyScroll(containerNode);
  }, [show, containerNode]);

  const selectOrg = (org) => {
    setTimeout(() => toggleShow(), 200);
    setTimeout(() => {
      setActiveCompany(org.id);
      history.push('/');
    }, 300);
  };

  const handleEditMyAccount = () => {
    setUserEditViewStatus(UserEditStatus.EDIT_SELF);
    toggleShow();
  };

  const handleLogOut = () => logOut();

  const handleUserEditViewHide = (action) => {
    setUserEditViewStatus(UserEditStatus.NONE);
  };

  const handleReturnTitleClick = () => setSelectOrgTab(!selectOrgTab);

  return (
    <>
      <Animation
        show={show}
        animation="slide_down"
        className={styles.animationContainer}
      >
        <div
          ref={containerCallbackRef}
          className={c(styles.container, (selectOrgTab && styles.disableScroll))}
          style={{ maxHeight: maxHeightContainer }}
        >
          <div className={displayMenuSection(!selectOrgTab, 'disabled_left')}>
            <div className={styles.headerContainer}>
              <h4>{user.fullName}</h4>
              <TextLink
                text={t.manageMyAccount}
                link={handleEditMyAccount}
                className={styles.editMyAccountTextLink}
              />
            </div>
            <div
              onClick={() => (user.companies.length > 1 ? setSelectOrgTab(!selectOrgTab) : undefined)}
              onKeyDown={() => null}
              className={styles.linkContainer}
            >
              {user.companies.length > 1 && (
                <div className={styles.linkItem}>
                  {t.chooseCompany}
                  <InlineSvg src="/caret-right.svg" />
                </div>
              )}
            </div>
            <div className={`${styles.currentOrgInfoContainer}`}>
              <span>{user.activeCompany.name}</span>
              <span>{user.activeCompany.organisationNumber}</span>
            </div>
            {isAdmin && (
              <NavLink
                exact
                to="/manage-users"
                onClick={toggleShow}
                className={styles.linkContainer}
                activeClassName={styles.active}
              >
                <span className={styles.linkItem}>
                  {t.manageUsers}
                </span>
              </NavLink>
            )}
            <NavLink
              exact
              to="/company-settings"
              onClick={toggleShow}
              className={styles.linkContainer}
              activeClassName={styles.active}
            >
              <span className={styles.linkItem}>
                {t.company_settings}
              </span>
            </NavLink>
            <NavLink
              exact
              to="/transactions/export"
              onClick={toggleShow}
              className={styles.linkContainer}
              activeClassName={styles.active}
            >
              <span className={styles.linkItem}>
                {t.exportAccountingDocuments}
              </span>
            </NavLink>
            <TextLink
              text={t.logOut}
              link={handleLogOut}
              className={styles.logOutTextLink}
            />
          </div>

          <div
            className={c(styles.selectOrgTab, displayMenuSection(selectOrgTab, 'disabled_right'))}
            style={{ height: heightOrgTab }}
          >
            <ReturnTitle
              title={t.chooseCompany}
              onClick={handleReturnTitleClick}
              display
              className={styles.selectOrgTabTitle}
            />
            <div className={styles.selectOrgList}>
              <ul>
                {user.companies.map((org) => (
                  <li key={org.id}>
                    <div
                      data-body-scroll-lock-ignore
                      onClick={() => selectOrg(org)}
                      className={c(styles.orgListItemContainer, (user.activeCompany.id === org.id && styles.selected))}
                    >
                      <div className={styles.orgInfoContainer}>
                        <span>{org.name}</span>
                        <span>{org.organisationNumber}</span>
                      </div>
                      {org.id === user.defaultCompany.id && (
                        <span className={styles.defaultOrgTag}>{t.managed}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Animation>

      <UserEditView
        selectedUser={user}
        organisation={user.activeCompany}
        status={userEditViewStatus}
        onHide={handleUserEditViewHide}
        tab={userEditViewStatusTab}
      />
    </>
  );
};

HeaderMenu.propTypes = {
  show: PropTypes.bool.isRequired,
  toggleShow: PropTypes.func.isRequired,
};

export default makeComponentTrashable(HeaderMenu);
